import * as DocumentPicker from 'expo-document-picker';
import { Paperclip } from 'lucide-react-native';
import { memo, useCallback } from 'react';
import { twJoin } from 'tailwind-merge';

import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { ERROR_MESSAGES } from '@/constants/errorMessages';
import {
  ALLOWED_FILE_TYPES,
  FILE_CONSTRAINTS,
  getMaxFileSize,
  getMaxFileSizeMB,
} from '@/constants/fileTypes';
import { useChatAnalytics } from '@/hooks/chat/useChatAnalytics';
import { useToaster } from '@/hooks/useToaster';
import type { UploadFile } from '@/types/uploadFile';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';
import { Sentry } from '@/util/sentry';

function getFileTypeLabel(mimeType: string): string {
  if (mimeType.startsWith('image/')) {
    return 'images';
  }

  if (mimeType.startsWith('video/')) {
    return 'videos';
  }

  return 'files';
}

type AttachmentActionProps = {
  files: UploadFile[];
  setFiles: React.Dispatch<React.SetStateAction<UploadFile[]>>;
  isSending: boolean;
};

/**
 * Paperclip button that opens the document picker and validates selected files.
 *
 * Encapsulates all file selection logic: picker invocation, size/type/duplicate
 * validation, state updates, and analytics tracking.
 */
export const AttachmentAction = memo(function AttachmentAction({
  files,
  setFiles,
  isSending,
}: AttachmentActionProps) {
  const { track } = useChatAnalytics();

  const toast = useToaster();

  const isDisabled = isSending || files.length >= FILE_CONSTRAINTS.MAX_FILE_COUNT;

  const handlePress = useCallback(async () => {
    if (files.length >= FILE_CONSTRAINTS.MAX_FILE_COUNT) {
      toast.showError({
        title: `Maximum ${FILE_CONSTRAINTS.MAX_FILE_COUNT} files allowed`,
        message: ERROR_MESSAGES.ATTACHMENTS.LIMIT_EXCEEDED.CHAT,
      });

      return;
    }

    try {
      const doc = await DocumentPicker.getDocumentAsync({
        multiple: false,
        type: ALLOWED_FILE_TYPES,
      });

      const file = doc.assets?.[0];

      if (doc.canceled || !file) {
        return;
      }

      const { size, mimeType, file: fileData, uri, name } = file;

      if (!size || !mimeType || !uri || !name) {
        return;
      }

      if (files.find((f) => f.name === name)) {
        toast.showError({
          title: 'File already added',
        });

        return;
      }

      const maxSize = getMaxFileSize(mimeType);

      const maxSizeMB = getMaxFileSizeMB(mimeType);

      if (size > maxSize) {
        toast.showError({
          title: `File too large (max ${maxSizeMB}MB for ${getFileTypeLabel(mimeType)})`,
        });

        return;
      }

      setFiles((prev) => {
        const newFiles = [
          ...prev,
          {
            name,
            size,
            mimeType,
            file: fileData,
            uri,
          },
        ];

        track(ANALYTICS_EVENTS.ATTACHMENTS_TRIGGERED, {
          current_file_count: newFiles.length,
          context: 'chat',
        });

        return newFiles;
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'AttachmentAction' },
      });

      toast.showError({
        title: 'Failed to select file',
      });
    }
  }, [files, setFiles, toast, track]);

  return (
    <Pressable
      disabled={isDisabled}
      onPress={handlePress}
      hitSlop={ICON_BUTTON_HIT_SLOP}
      className="h-10 w-10 items-center justify-center rounded-full active:opacity-70"
      {...getAccessibilityProps({ label: 'Attach a file', role: 'button' })}>
      <Icon
        as={Paperclip}
        size="lg"
        className={twJoin(isDisabled && 'text-typography-300', !isDisabled && 'text-primary-500')}
      />
    </Pressable>
  );
});
